import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { courtSlides, CourtSlide } from './courtSlides';

const AUTO_MS = 5500;
const FADE_MS = 700;

interface Props {
  slides?: CourtSlide[];
  autoPlay?: boolean;
  height?: { xs: number; md: number };
  /** Controlled slide index (pairs with onIndexChange). */
  index?: number;
  onIndexChange?: (index: number) => void;
  sx?: SxProps<Theme>;
}

export default function CourtCarousel({
  slides = courtSlides,
  autoPlay = true,
  height = { xs: 280, md: 420 },
  index: controlledIndex,
  onIndexChange,
  sx,
}: Props) {
  const [internalIndex, setInternalIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const count = slides.length;
  const isControlled = controlledIndex !== undefined;
  const index = isControlled ? controlledIndex : internalIndex;

  const applyIndex = useCallback(
    (next: number) => {
      const normalized = (next + count) % count;
      if (onIndexChange) onIndexChange(normalized);
      else setInternalIndex(normalized);
    },
    [count, onIndexChange],
  );

  const goTo = useCallback(
    (next: number) => {
      setFadeIn(false);
      window.setTimeout(() => {
        applyIndex(next);
        setFadeIn(true);
      }, FADE_MS * 0.45);
    },
    [applyIndex],
  );

  useEffect(() => {
    if (!isControlled) return undefined;
    setFadeIn(false);
    const id = window.setTimeout(() => setFadeIn(true), FADE_MS * 0.45);
    return () => window.clearTimeout(id);
  }, [controlledIndex, isControlled]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (!autoPlay || count < 2) return undefined;
    const id = window.setInterval(next, AUTO_MS);
    return () => window.clearInterval(id);
  }, [autoPlay, count, next]);

  const slide = slides[index];

  return (
    <Box
      sx={[
        {
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(13, 27, 42, 0.18)',
          height,
          bgcolor: '#0d1b2a',
          '@media (prefers-reduced-motion: reduce)': {
            '& .court-carousel-img': { transition: 'none !important', transform: 'none !important' },
          },
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
      role="region"
      aria-roledescription="carousel"
      aria-label="Facility photos"
    >
      {slides.map((s, i) => (
        <Box
          key={s.src}
          component="img"
          src={s.src}
          alt={s.alt}
          loading={i === 0 ? 'eager' : 'lazy'}
          className="court-carousel-img"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: i === index && fadeIn ? 1 : 0,
            transform: i === index && fadeIn ? 'scale(1.04)' : 'scale(1)',
            transition: `opacity ${FADE_MS}ms ease, transform ${AUTO_MS + 1000}ms ease-out`,
            zIndex: i === index ? 1 : 0,
          }}
        />
      ))}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          zIndex: 2,
          background: 'linear-gradient(to top, rgba(13,27,42,0.85) 0%, transparent 45%, rgba(13,27,42,0.25) 100%)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 3, p: { xs: 2, md: 3 } }}>
        <Typography variant="overline" sx={{ color: '#00c853', fontWeight: 700, letterSpacing: 2 }}>
          {slide.sport}
        </Typography>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>
          {slide.caption}
        </Typography>
      </Box>

      <IconButton
        aria-label="Previous slide"
        onClick={prev}
        sx={{
          position: 'absolute',
          left: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 4,
          bgcolor: 'rgba(255,255,255,0.15)',
          color: '#fff',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
        size="large"
      >
        <ChevronLeftIcon />
      </IconButton>
      <IconButton
        aria-label="Next slide"
        onClick={next}
        sx={{
          position: 'absolute',
          right: 8,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 4,
          bgcolor: 'rgba(255,255,255,0.15)',
          color: '#fff',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
        size="large"
      >
        <ChevronRightIcon />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 4,
          display: 'flex',
          gap: 1,
        }}
      >
        {slides.map((_, i) => (
          <Box
            key={i}
            component="button"
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === index ? 'true' : undefined}
            onClick={() => goTo(i)}
            sx={{
              width: i === index ? 28 : 10,
              height: 10,
              borderRadius: 5,
              border: 'none',
              p: 0,
              cursor: 'pointer',
              bgcolor: i === index ? '#00c853' : 'rgba(255,255,255,0.45)',
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
